import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS } from "../theme";
import Card from "../components/ui/Card";
import Avatar from "../components/ui/Avatar";
import Aviso from "../components/ui/Aviso";
import IconBadge from "../components/ui/IconBadge";
import RankingSaldo from "../components/ui/RankingSaldo";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import { horaLocal } from "../utils/tempo";

const STAT_CARDS = [
  { key: "funcionarios_ativos", label: "Funcionários ativos", icon: "users", tone: "primary" },
  { key: "postos_ativos", label: "Postos ativos", icon: "mapPin", tone: "success" },
  { key: "batidas_hoje", label: "Batidas hoje", icon: "clock", tone: "info" },
  { key: "pendencias_hora_extra", label: "Hora Extra Pendente", icon: "checkCircle", tone: "warning" },
];

const TONE_COLOR = {
  primary: COLORS.primary,
  success: COLORS.success,
  info: COLORS.info,
  warning: COLORS.warning,
};

const TIPO_LABEL = { entrada: "Entrada", saida: "Saída" };

const INTERVALO_ATUALIZACAO_MS = 30000;

export default function Dashboard() {
  const { usuario, token } = useAuth();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [saldoInfo, setSaldoInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // `silencioso` evita o "—" piscando nos cards a cada atualização em segundo
  // plano — só o primeiro carregamento (ou uma troca de tela) mostra o spinner.
  // O ranking de saldo usa o mesmo cálculo do Fechamento Mensal (mês
  // corrente) — busca junto, no mesmo ciclo de atualização automática.
  const carregar = useCallback(async (silencioso) => {
    if (!silencioso) setLoading(true);
    try {
      const [dashboardData, saldoData] = await Promise.all([
        apiFetch("GET", "/api/relatorios/dashboard", undefined, token),
        apiFetch("GET", "/api/relatorios/fechamento-mensal", undefined, token),
      ]);
      setDados(dashboardData);
      setSaldoInfo(saldoData);
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, [token]);

  // Sem isso, o gestor só via uma batida nova recarregando a página na mão —
  // nada avisava que os dados na tela estavam desatualizados.
  useEffect(() => {
    carregar();
    const id = setInterval(() => carregar(true), INTERVALO_ATUALIZACAO_MS);
    return () => clearInterval(id);
  }, [carregar]);

  const loadingSaldo = loading || !saldoInfo;
  const quemPrecisaAtencao = (saldoInfo?.funcionarios || [])
    .filter((f) => f.dias_falta > 0 || f.atrasos_qtd > 0)
    .sort((a, b) => (b.dias_falta * 2 + b.atrasos_qtd) - (a.dias_falta * 2 + a.atrasos_qtd));

  return (
    <div>
      <div style={{ marginBottom: 28, display: "flex", alignItems: "center", gap: 14 }}>
        <Avatar nome={usuario?.nome} size={46} />
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Olá, {usuario?.nome?.split(" ")[0]}
          </h1>
          <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
            Aqui está um resumo do ponto da sua equipe hoje.
          </p>
        </div>
      </div>

      {!loading && dados?.feriado_hoje && (
        <Aviso tone="info" icon="calendar">
          Hoje é feriado ({dados.feriado_hoje}) — ninguém é cobrado de bater ponto.
        </Aviso>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
        {STAT_CARDS.map((s) => {
          const cor = TONE_COLOR[s.tone];
          return (
            <Card key={s.key}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ fontSize: 12.5, color: COLORS.textMid, fontWeight: 600 }}>{s.label}</div>
                <IconBadge icon={s.icon} size={30} cor={cor} />
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>
                {loading || !dados ? "—" : dados[s.key]}
              </div>
            </Card>
          );
        })}
      </div>

      {!loading && dados && dados.presenca_hoje.esperados > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 12.5, color: COLORS.textMid, fontWeight: 600 }}>Presença hoje</span>
            <span style={{ fontSize: 13.5, fontWeight: 800, color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>
              {dados.presenca_hoje.bateram} de {dados.presenca_hoje.esperados}
              {" "}({Math.round((dados.presenca_hoje.bateram / dados.presenca_hoje.esperados) * 100)}%)
            </span>
          </div>
          <div style={{ height: 8, borderRadius: 100, background: COLORS.bg, overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${Math.round((dados.presenca_hoje.bateram / dados.presenca_hoje.esperados) * 100)}%`,
                background: dados.presenca_hoje.bateram === dados.presenca_hoje.esperados ? COLORS.success : COLORS.primary,
                borderRadius: 100,
                transition: "width .3s",
              }}
            />
          </div>
          <p style={{ fontSize: 11.5, color: COLORS.textLight, margin: "8px 0 0" }}>
            Considera só quem tem escala prevista pra hoje (segunda-sexta ou segunda-sábado).
          </p>
        </Card>
      )}

      {!loading && dados?.pendencias_hora_extra > 0 && (
        <Card
          onClick={() => navigate("/hora-extra")}
          style={{
            display: "flex", alignItems: "center", gap: 14, marginBottom: 20, cursor: "pointer",
            background: COLORS.warningBg, border: `1px solid ${COLORS.warning}33`,
          }}
        >
          <IconBadge icon="checkCircle" size={34} cor={COLORS.warning} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>
              {dados.pendencias_hora_extra} {dados.pendencias_hora_extra === 1 ? "hora extra esperando" : "horas extras esperando"} aprovação
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMid }}>Clique para revisar</div>
          </div>
          <span style={{ fontSize: 18, color: COLORS.warning }}>›</span>
        </Card>
      )}

      {saldoInfo?.batidas_incompletas?.length > 0 && (
        <Card
          onClick={() => navigate(`/funcionarios/historico?funcionario_id=${saldoInfo.batidas_incompletas[0].funcionario_id}`)}
          style={{
            display: "flex", alignItems: "center", gap: 14, marginBottom: 20, cursor: "pointer",
            background: COLORS.warningBg, border: `1px solid ${COLORS.warning}33`,
          }}
        >
          <IconBadge icon="alertCircle" size={34} cor={COLORS.warning} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>
              {saldoInfo.batidas_incompletas.length === 1
                ? "1 batida incompleta esse mês"
                : `${saldoInfo.batidas_incompletas.length} batidas incompletas esse mês`}
              {" "}({new Set(saldoInfo.batidas_incompletas.map((b) => b.funcionario_id)).size === 1
                ? saldoInfo.batidas_incompletas[0].nome
                : `${new Set(saldoInfo.batidas_incompletas.map((b) => b.funcionario_id)).size} funcionários`})
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMid }}>
              Bateu só entrada ou só saída em um dia — não entra no saldo até corrigir. Clique para revisar.
            </div>
          </div>
          <span style={{ fontSize: 18, color: COLORS.warning }}>›</span>
        </Card>
      )}

      {saldoInfo && (saldoInfo.maiores_saldos.length > 0 || saldoInfo.menores_saldos.length > 0) && (
        <div style={{ marginBottom: 20 }}>
          <RankingSaldo maiores={saldoInfo.maiores_saldos} menores={saldoInfo.menores_saldos} />
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
        <Card style={{ padding: 0 }}>
          <div
            onClick={() => navigate("/atrasos-faltas")}
            style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, cursor: "pointer", borderBottom: quemPrecisaAtencao.length > 0 ? `1px solid ${COLORS.border}` : "none" }}
          >
            <IconBadge icon="alertCircle" size={32} cor={quemPrecisaAtencao.length > 0 ? COLORS.danger : COLORS.success} />
            <div style={{ flex: 1 }}>
              <h2 style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, margin: 0 }}>Quem precisa de atenção</h2>
              <p style={{ fontSize: 12, color: COLORS.textMid, margin: "3px 0 0" }}>
                Faltas sem atestado (sujeitas a desconto) ou atrasos.
              </p>
            </div>
            {!loadingSaldo && (
              <div style={{ fontSize: 26, fontWeight: 900, color: quemPrecisaAtencao.length > 0 ? COLORS.danger : COLORS.success, fontVariantNumeric: "tabular-nums" }}>
                {quemPrecisaAtencao.length}
              </div>
            )}
            <span style={{ fontSize: 18, color: COLORS.textLight }}>›</span>
          </div>

          {quemPrecisaAtencao.length > 0 && (
            <div>
              {quemPrecisaAtencao.slice(0, 5).map((f, i) => (
                <div
                  key={f.funcionario_id}
                  onClick={() => navigate("/atrasos-faltas")}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, cursor: "pointer",
                    padding: "10px 20px", borderBottom: i < Math.min(4, quemPrecisaAtencao.length - 1) ? `1px solid ${COLORS.border}` : "none",
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {f.nome}
                  </span>
                  <span style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    {f.dias_falta > 0 && (
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: COLORS.danger, background: COLORS.dangerBg, borderRadius: 5, padding: "2px 7px", whiteSpace: "nowrap" }}>
                        {f.dias_falta} falta{f.dias_falta > 1 ? "s" : ""}
                      </span>
                    )}
                    {f.atrasos_qtd > 0 && (
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: COLORS.warning, background: COLORS.warningBg, borderRadius: 5, padding: "2px 7px", whiteSpace: "nowrap" }}>
                        {f.atrasos_qtd} atraso{f.atrasos_qtd > 1 ? "s" : ""}
                      </span>
                    )}
                  </span>
                </div>
              ))}
              {quemPrecisaAtencao.length > 5 && (
                <button
                  onClick={() => navigate("/atrasos-faltas")}
                  style={{ display: "block", width: "100%", background: "none", border: "none", color: COLORS.primary, fontSize: 12.5, fontWeight: 700, cursor: "pointer", padding: "10px 20px", textAlign: "center" }}
                >
                  Ver todos ({quemPrecisaAtencao.length})
                </button>
              )}
            </div>
          )}
        </Card>

        <Card style={{ padding: 0 }}>
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 12 }}>
            <IconBadge icon="clock" size={32} cor={COLORS.warning} />
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, margin: 0 }}>Atividade de hoje</h2>
              <p style={{ fontSize: 12, color: COLORS.textMid, margin: "3px 0 0" }}>
                Toda batida de hoje, mais recente primeiro — mostra atraso ou adiantamento acima de 5 minutos.
              </p>
            </div>
          </div>
          {loading || !dados ? (
            <EstadoCarregando />
          ) : dados.atividade_hoje.length === 0 ? (
            <div style={{ padding: 24, fontSize: 13.5, color: COLORS.textMid }}>
              Nenhuma batida registrada ainda hoje.
            </div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              {dados.atividade_hoje.map((a, i) => (
                <div
                  key={`${a.funcionario_id}-${a.tipo}-${a.horario}`}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "12px 20px", borderBottom: i < dados.atividade_hoje.length - 1 ? `1px solid ${COLORS.border}` : "none",
                  }}
                >
                  <div>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: COLORS.text }}>{a.nome}</span>
                    <span style={{ marginLeft: 8, fontSize: 11.5, color: COLORS.textLight }}>{TIPO_LABEL[a.tipo] || a.tipo}</span>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 13, color: COLORS.text, fontVariantNumeric: "tabular-nums" }}>{horaLocal(a.horario)}</div>
                    {a.diferenca_min !== null && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: a.diferenca_min > 0 ? COLORS.warning : COLORS.textLight }}>
                        {a.diferenca_min > 0 ? `+${a.diferenca_min}min atraso` : `${Math.abs(a.diferenca_min)}min adiantado`}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
